import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FileExportIcon } from "@components/icons";
import { Button, Dropdown } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import TreeDetailPDF from "./TreeDetailPDF";
import Spinner from "@components/Spinner/Spinner";

import { getTree } from "@modules/Trees/services/treeCrud";

export async function loadTreesWithImages(treeIds: number[]) {
  const results = await Promise.all(
    treeIds.map(async (id) => {
      try {
        const tree = await getTree(id);
        return { ...tree };
      } catch (err) {
        console.error(`Failed to load tree ${id}`, err);
        return null;
      }
    })
  );

  // Remove any failed trees
  return results.filter((t) => t !== null);
}

type TreeWithImages = {
  id: number;
  treeImages?: any[];
  leafImages?: any[];
  [key: string]: any;
};

const TreePDFExportPage = ({
  title,
  trees,
  renderMode = "button",
}: {
  title: string;
  trees: TreeWithImages[];
  renderMode?: "button" | "menu";
}) => {
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const handleExport = async () => {
    setIsLoading(true); // Start loading
    const pdf = new jsPDF("p", "mm", "a4");

    for (let i = 0; i < refs.current.length; i++) {
      const el = refs.current[i];
      if (!el) continue;
      await waitForLeafletTiles(el); // ⏳ warte bis alle Tiles geladen

      const canvas = await html2canvas(el, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save(`${title}.pdf`);
    setIsLoading(false); // Stop loading
  };

  const { t } = useTranslation();
  const exportLabel = isLoading ? t("downloading") : t("download_tree_details");

  return (
    <>
      {trees.length > 0 && renderMode === "button" && (
        <Button onClick={handleExport} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner height={"5px"} onRight={true}>
                {" "}
                {t("downloading")}
              </Spinner>
            </>
          ) : (
            <>
              <FileExportIcon /> {t("download_tree_details")}
            </>
          )}
        </Button>
      )}
      {trees.length > 0 && renderMode === "menu" && (
        <Dropdown.Item onClick={handleExport} disabled={isLoading}>
          <FileExportIcon /> {exportLabel}
        </Dropdown.Item>
      )}

      <div style={{ position: "absolute", top: "-9999px" }}>
        {trees.map((tree, index) => (
          <div
            key={tree.id}
            ref={(el) => (refs.current[index] = el)}
            style={{
              top: "-9999px",
              position: "absolute",
              left: "-9999px",
              width: "794px",
              height: "auto",
              visibility: "visible",
            }}
          >
            <TreeDetailPDF tree={tree} treeImages={tree.treeImages} leafImages={tree.leafImages} />
          </div>
        ))}
      </div>
    </>
  );
};

export default TreePDFExportPage;

export const waitForLeafletTilesOld = (container: HTMLElement): Promise<void> => {
  return new Promise<void>((resolve) => {
    const tileImages = Array.from(
      container.querySelectorAll("img.leaflet-tile")
    ) as HTMLImageElement[];

    if (tileImages.length === 0) return resolve(); // fallback

    let loaded = 0;

    tileImages.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        loaded++;
        if (loaded === tileImages.length) resolve();
      } else {
        img.addEventListener("load", () => {
          loaded++;
          if (loaded === tileImages.length) resolve();
        });
        img.addEventListener("error", () => {
          loaded++;
          if (loaded === tileImages.length) resolve();
        });
      }
    });
  });
};

const waitForLeafletTiles = (mapElement: HTMLElement): Promise<void> => {
  return new Promise<void>((resolve) => {
    const maps = mapElement.querySelectorAll(".leaflet-map");
    let loadedMaps = 0;

    if (maps.length === 0) {
      resolve();
      return;
    }

    maps.forEach((map) => {
      const leafletMap = (window as any).L.map(map);
      leafletMap.whenReady(() => {
        leafletMap.on("load", () => {
          loadedMaps++;
          if (loadedMaps === maps.length) {
            resolve();
          }
        });

        // Fallback in case the load event doesn't fire
        setTimeout(() => {
          loadedMaps++;
          if (loadedMaps === maps.length) {
            resolve();
          }
        }, 5000);
      });
    });
  });
};
